import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../hooks/useSubscription';
import { useAuth } from '../../hooks/useAuth';
import { getBestMatchingPlan } from '../../utils/planMapping';
import QuotaDisplay from './QuotaDisplay';
import { toast } from 'react-toastify';

const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const { 
    currentSubscription, 
    currentQuota,
    plans, 
    loading, 
    error,
    loadUserSubscription,
    loadPlans,
    handleCancelBillingSubscription,
    handleSetBillingAutoRenew,
    subscriptionRefreshTrigger,
    triggerSubscriptionRefresh,
    syncQuotaWithSubscription,
    resetQuotaToFree
  } = useSubscription();
  const { user } = useAuth();
  
  // Check if user is admin
  const userRoles = user?.roles || [];
  const isAdmin = userRoles.includes('ADMIN');
  
  // Debug logging to help identify data structure issues (only log once per data change)
  const loggedRef = useRef({ subscription: null, plans: null });
  
  // Load subscription and plans data when component mounts
  React.useEffect(() => {
    if (user?.id) {
      loadUserSubscription();
      loadPlans(); // Also load plans to ensure we have plan names
    }
  }, [user?.id, loadUserSubscription, loadPlans]);
  
  // Debug logging useEffect - must be at the top level
  React.useEffect(() => {
    if (currentSubscription && plans && !loading) {
      const subscriptionChanged = JSON.stringify(currentSubscription) !== loggedRef.current.subscription;
      const plansChanged = JSON.stringify(plans) !== loggedRef.current.plans;
      
      if (subscriptionChanged || plansChanged) {
        loggedRef.current.subscription = JSON.stringify(currentSubscription);
        loggedRef.current.plans = JSON.stringify(plans);
      }
    }
  }, [currentSubscription, plans, loading]);

  // Respond to subscription refresh triggers
  React.useEffect(() => {
    if (subscriptionRefreshTrigger && user?.id) {
      console.log('🔄 SubscriptionManagement: Responding to subscription refresh trigger');
      // These are async functions, but we don't need to await them in useEffect
      loadUserSubscription().catch(console.error);
      loadPlans();
    }
  }, [subscriptionRefreshTrigger, user?.id]);

  // Sync quota data with subscription plan when subscription changes
  React.useEffect(() => {
    if (currentSubscription && currentQuota) {
      if (currentSubscription.status === 'CANCELLED' || currentSubscription.status === 'EXPIRED') {
        console.log('🔄 Resetting quota to free tier due to cancelled/expired subscription');
        resetQuotaToFree();
      } else if (currentSubscription.status === 'ACTIVE') {
        console.log('🔄 Syncing quota with active subscription plan');
        syncQuotaWithSubscription();
      }
    }
  }, [currentSubscription?.status, currentSubscription?.plan?.storageLimitSeconds, currentSubscription?.plan?.freePurchaseSecondsPerMonth, syncQuotaWithSubscription, resetQuotaToFree]);

  // Use the new plan mapping utility to get the best matching plan - MUST be before any early returns
  const bestPlan = React.useMemo(() => {
    return getBestMatchingPlan(currentSubscription, currentQuota, plans);
  }, [currentSubscription, currentQuota, plans]);

  // Handle different subscription states - MUST be before smart polling effect
  const isPending = currentSubscription?.status === 'PENDING';
  const isIncomplete = currentSubscription?.status === 'DEFAULT_INCOMPLETE';
  const hasClientSecret = !!currentSubscription?.clientSecret;
  const hasSubscriptionId = !!currentSubscription?.subscriptionId;

  // Smart polling for processing subscriptions - MUST be before any early returns
  React.useEffect(() => {
    if (!user?.id) return;
    if (!currentSubscription) return;
    if (!(isPending || isIncomplete)) return;

    let cancelled = false;
    let attempts = 0;
    let timer;

    const poll = async () => {
      attempts += 1;
      try {
        await loadUserSubscription();
      } catch (e) {
        // ignore transient failures
        console.log('Polling attempt failed (transient):', e.message);
      }
      if (cancelled) return;

      // stop conditions: the store will have new status by now
      const nowStatus = (() => {
        // get latest from props (component re-renders keep this fresh)
        return currentSubscription?.status;
      })();

      if (nowStatus === 'ACTIVE' || attempts >= 20) { // ~ up to ~40–60s depending on backoff
        console.log(`🔄 Polling stopped: status=${nowStatus}, attempts=${attempts}`);
        return;
      }

      const nextDelay = Math.min(1000 * Math.pow(1.5, attempts), 8000);
      console.log(`🔄 Next poll in ${nextDelay}ms (attempt ${attempts + 1})`);
      timer = setTimeout(poll, nextDelay);
    };

    // kick off with a short delay
    console.log('🔄 Starting smart polling for processing subscription', { 
      status: currentSubscription?.status, 
      userId: user?.id 
    });
    timer = setTimeout(poll, 1200);
    return () => { 
      cancelled = true; 
      clearTimeout(timer); 
      console.log('🔄 Smart polling cancelled');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, currentSubscription?.status]);

  const handleUpgradePlan = () => {
    navigate('/pricing');
  };

  // Handle subscription cancellation
  const handleCancelSubscription = async () => {
    if (!currentSubscription.stripeSubscriptionId) {
      toast.error('No Stripe subscription found. Cannot cancel subscription.');
      return;
    }

    if (window.confirm('Are you sure you want to cancel your subscription? This action cannot be undone.')) {
      try {
        const result = await handleCancelBillingSubscription(
          currentSubscription.stripeSubscriptionId,
          user.id
        );
        
        if (result.payload) {
          toast.success('Subscription cancelled successfully!');
          // Refresh the subscription data to show updated status
          window.location.reload();
        } else {
          toast.error('Failed to cancel subscription. Please try again.');
        }
        
      } catch (error) {
        console.error('Subscription cancellation error:', error);
        toast.error(`Failed to cancel subscription: ${error.message || 'Unknown error'}`);
      }
    }
  };

  // Handle auto-renew toggle
  const handleToggleAutoRenew = async () => {
    if (!currentSubscription.stripeSubscriptionId) {
      toast.error('No Stripe subscription found. Cannot toggle auto-renew.');
      return;
    }

    const newAutoRenewState = !currentSubscription.autoRenew;
    const action = newAutoRenewState ? 'enable' : 'disable';
    
    if (window.confirm(`Are you sure you want to ${action} auto-renew for your subscription?`)) {
      try {
        const result = await handleSetBillingAutoRenew(
          currentSubscription.stripeSubscriptionId,
          newAutoRenewState
        );
        
        if (result.payload) {
          toast.success(`Auto-renew ${action}d successfully!`);
          // Refresh the subscription data to show updated auto-renew state
          window.location.reload();
        } else {
          toast.error(`Failed to ${action} auto-renew. Please try again.`);
        }
        
      } catch (error) {
        console.error('Auto-renew toggle error:', error);
        toast.error(`Failed to ${action} auto-renew: ${error.message || 'Unknown error'}`);
      }
    }
  };

  // Note: Auto-renew functionality requires stripe subscription ID
  // This would need to be implemented using the billing endpoints

  if (loading) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-8 md:text-5xl">
          My Subscription
        </h1>
        <div className="text-center text-lg text-gray-600 p-8">Loading subscription details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-8 md:text-5xl">
          My Subscription
        </h1>
        <div className="text-center text-lg text-red-600 p-8">Error loading subscription: {error}</div>
      </div>
    );
  }

  if (!currentSubscription) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-8 md:text-5xl">
          My Subscription
        </h1>
        <div className="text-center text-lg text-gray-600 p-8">
          You don't have an active subscription.
          <br />
          <a href="/subscription" className="text-teal-600 hover:text-teal-700 underline">
            View available plans
          </a>
        </div>
      </div>
    );
  }

  // Handle different subscription data structures and states
  const subscriptionPlanCode = currentSubscription?.plan?.planCode || 
                              currentSubscription?.planCode || 
                              currentSubscription?.plan;
  
  // Determine the actual plan to display based on subscription status
  let actualPlanCode = subscriptionPlanCode;
  let actualPlan = null;
  
  if (currentSubscription?.status === 'CANCELLED' || currentSubscription?.status === 'EXPIRED') {
    // Show free plan for canceled/expired subscriptions
    actualPlanCode = 'FREE';
    actualPlan = plans?.find(p => p.planCode === 'FREE') || {
      planCode: 'FREE',
      name: 'Free Plan',
      description: '4 hours storage, no monthly credits',
      type: 'FREE'
    };
  } else if (currentSubscription?.status === 'ACTIVE') {
    // Show the actual subscription plan for active subscriptions
    actualPlan = plans?.find(p => p.planCode === subscriptionPlanCode);
  }
  
  const displayPlan = actualPlan || bestPlan;

  // Safely access subscription dates with fallbacks
  const startDate = currentSubscription.startsAt ? 
    new Date(currentSubscription.startsAt).toLocaleDateString() : 
    'Not specified';
  
  const endDate = currentSubscription.endsAt ? 
    new Date(currentSubscription.endsAt).toLocaleDateString() : 
    'Not specified';
  
  const isActive = currentSubscription.status === 'ACTIVE';



  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-4xl font-bold text-center text-gray-800 mb-8 md:text-5xl">
        My Subscription
      </h1>
      
      <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <div className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${
              currentSubscription.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
              currentSubscription.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
              currentSubscription.status === 'EXPIRED' ? 'bg-gray-100 text-gray-800' :
              'bg-yellow-100 text-yellow-800'
            }`}>
              {currentSubscription.status}
            </div>
            
            {/* Smart polling indicator */}
            {(isPending || isIncomplete) && (
              <div className="flex items-center space-x-2 text-sm text-blue-600">
                <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="text-xs">Auto-refreshing...</span>
              </div>
            )}
          </div>
          
          <button
            onClick={() => {
              try {
                triggerSubscriptionRefresh().catch(console.error);
              } catch (error) {
                console.error('Failed to refresh subscription data:', error);
              }
            }}
            disabled={loading}
            className="flex items-center space-x-2 px-3 py-2 text-sm bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
            title="Refresh subscription data"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Refresh</span>
          </button>
        </div>
        
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {isPending || isIncomplete ? 'Subscription Processing...' : 
             (currentSubscription?.status === 'CANCELLED' || currentSubscription?.status === 'EXPIRED') ? 'Free Plan' :
             (displayPlan?.name || currentSubscription?.plan?.name || actualPlanCode || 'Unknown Plan')}
          </h2>
          <p className="text-gray-600">
            {isPending ? 'Your subscription is being processed. Please wait while we confirm your payment.' : 
             isIncomplete ? 'Payment confirmed! Your subscription is being activated via webhook processing.' :
             (currentSubscription?.status === 'CANCELLED' || currentSubscription?.status === 'EXPIRED') ? 'Your subscription has been canceled. You now have access to the free plan features.' :
             (displayPlan?.description || currentSubscription?.plan?.description || 'Subscription plan')}
          </p>
        </div>

        {(isPending || isIncomplete) ? (
          // Show processing subscription details
          <div className="space-y-4 mb-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center">
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-3"></div>
                <span className="text-blue-800 font-medium">
                  {isPending ? 'Processing Payment' : 'Activating Subscription'}
                </span>
              </div>
              <p className="text-blue-700 text-sm mt-2">
                {isPending ? 'Your subscription is being processed. This usually takes a few moments.' :
                 'Payment confirmed! Your subscription is being activated via webhook processing.'}
              </p>
              <p className="text-blue-600 text-xs mt-1">
                💡 This page will automatically refresh when your subscription becomes active.
              </p>
            </div>
            
            <div className="flex justify-between items-center py-3 border-b border-gray-200">
              <span className="font-semibold text-gray-700">Status:</span>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                isPending ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
              }`}>
                {isPending ? 'PENDING' : 'DEFAULT_INCOMPLETE (Processing)'}
              </span>
            </div>
            
            {hasSubscriptionId && (
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="font-semibold text-gray-700">Subscription ID:</span>
                <span className="text-gray-800 font-mono text-sm">{currentSubscription.subscriptionId}</span>
              </div>
            )}
            
            {hasClientSecret && (
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="font-semibold text-gray-700">Payment Intent:</span>
                <span className="text-gray-800 font-mono text-sm">Processing...</span>
              </div>
            )}
          </div>
        ) : (currentSubscription?.status === 'CANCELLED' || currentSubscription?.status === 'EXPIRED') ? (
          // Show canceled subscription details
          <div className="space-y-4 mb-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-red-800 font-medium">
                  Subscription Canceled
                </span>
              </div>
              <p className="text-red-700 text-sm mt-2">
                Your subscription has been canceled. You now have access to the free plan features.
              </p>
              <p className="text-red-600 text-xs mt-1">
                💡 You can resubscribe anytime to regain access to premium features.
              </p>
            </div>
            
            <div className="flex justify-between items-center py-3 border-b border-gray-200">
              <span className="font-semibold text-gray-700">Previous Plan:</span>
              <span className="text-gray-800">{currentSubscription?.plan?.name || 'Unknown'}</span>
            </div>
            
            <div className="flex justify-between items-center py-3 border-b border-gray-200">
              <span className="font-semibold text-gray-700">Canceled Date:</span>
              <span className="text-gray-800">{endDate}</span>
            </div>
            
            <div className="flex justify-between items-center py-3">
              <span className="font-semibold text-gray-700">Current Plan:</span>
              <span className="text-gray-800 font-semibold">Free Plan</span>
            </div>
          </div>
        ) : (
          // Show active subscription details
          <div className="space-y-4 mb-8">
            <div className="flex justify-between items-center py-3 border-b border-gray-200">
              <span className="font-semibold text-gray-700">Start Date:</span>
              <span className="text-gray-800">{startDate}</span>
            </div>
            
            <div className="flex justify-between items-center py-3 border-b border-gray-200">
              <span className="font-semibold text-gray-700">End Date:</span>
              <span className="text-gray-800">{endDate}</span>
            </div>
            
            <div className="flex justify-between items-center py-3 border-b border-gray-200">
              <span className="font-semibold text-gray-700">Auto-Renew:</span>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                currentSubscription.autoRenew ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {currentSubscription.autoRenew ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            
            <div className="flex justify-between items-center py-3">
              <span className="font-semibold text-gray-700">Status:</span>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleUpgradePlan}
            disabled={isPending || isIncomplete}
            className={`flex-1 px-6 py-3 border-none rounded-lg text-base font-semibold transition-all duration-300 ${
              (isPending || isIncomplete)
                ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                : 'bg-blue-600 text-white cursor-pointer hover:bg-blue-700 hover:transform hover:-translate-y-1 hover:shadow-lg'
            }`}
          >
            {(isPending || isIncomplete) ? 'Processing...' : 'Upgrade Plan'}
          </button>
          
          {/* Cancel Subscription Button - Only show if we have stripeSubscriptionId */}
          {currentSubscription.stripeSubscriptionId && (
            <button
              onClick={() => handleCancelSubscription()}
              disabled={isPending || isIncomplete}
              className={`flex-1 px-6 py-3 border-none rounded-lg text-base font-semibold transition-all duration-300 ${
                (isPending || isIncomplete)
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                  : 'bg-red-600 text-white cursor-pointer hover:bg-red-700 hover:transform hover:-translate-y-1 hover:shadow-lg'
              }`}
            >
              {(isPending || isIncomplete) ? 'Processing...' : 'Cancel Subscription'}
            </button>
          )}
          
          {/* Toggle Auto-Renew Button - Only show if we have stripeSubscriptionId */}
          {currentSubscription.stripeSubscriptionId && (
            <button
              onClick={() => handleToggleAutoRenew()}
              disabled={isPending || isIncomplete}
              className={`flex-1 px-6 py-3 border-none rounded-lg text-base font-semibold transition-all duration-300 ${
                (isPending || isIncomplete)
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                  : currentSubscription.autoRenew
                  ? 'bg-orange-600 text-white cursor-pointer hover:bg-orange-700 hover:transform hover:-translate-y-1 hover:shadow-lg'
                  : 'bg-green-600 text-white cursor-pointer hover:bg-green-700 hover:transform hover:-translate-y-1 hover:shadow-lg'
              }`}
            >
              {(isPending || isIncomplete) ? 'Processing...' : 
               currentSubscription.autoRenew ? 'Disable Auto-Renew' : 'Enable Auto-Renew'}
            </button>
          )}
        </div>
        
        {/* Resubscribe Button for Canceled Subscriptions */}
        {(currentSubscription?.status === 'CANCELLED' || currentSubscription?.status === 'EXPIRED') && (
          <div className="mt-6">
            <button
              onClick={() => navigate('/pricing')}
              className="w-full px-6 py-3 bg-blue-600 text-white border-none rounded-lg text-base font-semibold transition-all duration-300 cursor-pointer hover:bg-blue-700 hover:transform hover:-translate-y-1 hover:shadow-lg"
            >
              Resubscribe to Premium Plan
            </button>
            <p className="text-sm text-gray-600 mt-2 text-center">
              Ready to get back to premium features? Choose a plan that works for you.
            </p>
          </div>
        )}
        
        <p className="text-sm text-gray-600 mt-2">
          {isPending 
            ? 'Your subscription is being processed. Once confirmed, you\'ll have full access to your plan features.'
            : isIncomplete
            ? 'Payment confirmed! Your subscription is being activated via webhook processing. This usually takes a few moments.'
            : (currentSubscription?.status === 'CANCELLED' || currentSubscription?.status === 'EXPIRED')
            ? 'Your subscription has been canceled. You can resubscribe anytime to regain access to premium features.'
            : currentSubscription.stripeSubscriptionId 
            ? 'You can manage your subscription, cancel, or toggle auto-renew using the buttons above.'
            : 'Subscription management features will be available once your subscription is fully activated.'
          }
        </p>
      </div>

      {/* Storage Quota Display - Visible to all users */}
      {isActive && (
        <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
          <QuotaDisplay userId={user?.id} />
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
